'use client'

import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { toast } from '@/lib/toast'
import { useUpdateAccount } from '@/service/account.hook'
import { PublicAccount } from '@/types/generated/common/account'

// Rename is the only update_account field available to non-admin members,
// so non-admins get this lightweight dialog instead of the credentials form.
export function RenameAccountDialog({
  account,
  open,
  onOpenChange,
}: {
  account: PublicAccount
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useTranslations('settings')
  const [name, setName] = useState(account.name)

  const { mutate: updateAccount, isPending } = useUpdateAccount({
    onSuccess: (result) => {
      toast.success(result.message)
      onOpenChange(false)
    },
    onError: (error) => toast.error(error.message || t('failedRenameAccount')),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || trimmed === account.name) {
      onOpenChange(false)
      return
    }
    updateAccount({ accountId: account.accountId, name: trimmed })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('renameAccount')}</DialogTitle>
          <DialogDescription>{t('renameAccountDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('accountName')}
          />
          <DialogFooter>
            <Button type="submit" size="sm" disabled={isPending || !name.trim()}>
              {isPending ? t('saving') : t('save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
